import { useNavigate, useParams } from 'react-router-dom';
import { Fragment, useEffect, useState } from 'react';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import { fetchProductBySlug } from '../../../../../store/products/productReducer';
import { fetchBusinesses } from "../../../../../store/business/businessReducer";
import { useDispatch, useSelector } from 'react-redux';
import Spinner from '../../../../../components/Spinner/Index';
import { addToCart } from '../../../../../store/cart/cartReducer';

const FaqType = {
    question: '',
    answer: '',
};

const ProductShow = () => {
    const { slug } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { businesses } = useSelector((state) => state.business);
    const { product, isLoading, isError, message } = useSelector((state) => state.products);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [selectedBusiness, setSelectedBusiness] = useState("");

    useEffect(() => {
        if (slug) {
            dispatch(fetchProductBySlug(slug));
        }
        dispatch(fetchBusinesses())
    }, [dispatch, slug]);

    useEffect(() => {
        if (product?.data?.details?.pricing?.packages?.length > 0) {
            // Set the default package to the first package
            setSelectedPackage(product.data.details.pricing.packages[0]);
        }
    }, [product]);

    if (!product) {
        return <Spinner />;
    }

    const resolveImagePath = (path) => {
        try {
            console.log(path);
            return require(`../../../../../${path}`);
        } catch {
            return "https://via.placeholder.com/300"; // Fallback to a placeholder if image not found
        }
    };

    const handlePackageChange = (event) => {
        const selectedPackageName = event.target.value;

        // Ensure product, details, and pricing exist before accessing them
        const packageDetails = product?.data?.details?.pricing?.packages?.find(
            (pkg) => pkg.name === selectedPackageName
        );

        if (packageDetails) {
            setSelectedPackage(packageDetails);
        }
    };


    const handleAddToCart = async () => {
        if (!selectedPackage || !product?.data) {
            console.error("No package selected or product data missing");
            return;
        }

        const productData = product.data; // Explicitly destructure product data

        const cartData = {
            productId: productData._id, // Correctly access the product ID
            packageName: selectedPackage.name, // Access the selected package name
            packagePrice: selectedPackage.price, // Access the selected package price
            quantity: 1, // Set default quantity
            businessId: selectedBusiness,
        };

        try {
            await dispatch(addToCart(cartData)); // Dispatch action to add to cart
            navigate("/user/cart");
        } catch (error) {
            console.error("Error adding to cart:", error.message);
        }
    };


    const productData = product.data;

    return (
        <div className="bg-white">
            <div className="mx-auto px-4 sm:px-6 lg:max-w-7xl lg:px-8">
                {/* Product */}
                <div className="lg:grid lg:grid-cols-7 lg:grid-rows-1 lg:gap-x-8 lg:gap-y-10 xl:gap-x-16">
                    {/* Product image */}
                    <div className="lg:col-span-4 lg:row-end-1">
                        <img
                            alt={productData.name}
                            src={resolveImagePath(productData.image_url)}
                            className="aspect-[4/3] w-full rounded-lg bg-gray-100 object-cover"
                        />
                    </div>

                    {/* Product details */}
                    <div className="mx-auto mt-14 max-w-2xl sm:mt-16 lg:col-span-3 lg:row-span-2 lg:row-end-2 lg:mt-0 lg:max-w-none">
                        <div className="flex flex-col-reverse">
                            <div className="mt-4">
                                <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                                    {productData.name}
                                </h1>
                            </div>
                        </div>

                        <p className="mt-6 text-gray-500">{productData.description}</p>

                        {/* Business Selector */}
                        {businesses.length > 0 ? (
                            <div className="mt-6">
                                <label htmlFor="business" className="block text-sm font-medium text-gray-700">
                                    Select Business:
                                </label>
                                <select
                                    id="business"
                                    name="business"
                                    value={selectedBusiness}
                                    onChange={(e) => setSelectedBusiness(e.target.value)}
                                    className="mt-1 py-3 px-3 block w-full rounded-md border border-gray-400 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm hover:border-green-400"
                                >
                                    <option value="">Select a business</option>
                                    {businesses.map((business) => (
                                        <option key={business._id} value={business._id}>
                                            {business.businessInformation.businessName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        ) : (
                            <p className="mt-6 text-red-500">
                                You have no existing businesses in the system. Please{" "}
                                <a href="/user/businesses/create" className="text-green-600 underline">
                                    add a business
                                </a>{" "}
                                on your profiel before proceeding.
                            </p>
                        )}

                        {/* Package Selector */}
                        {productData.details?.pricing?.packages?.length > 0 && (
                            <div className="mt-6">
                                <label htmlFor="package" className="block text-sm font-medium text-gray-700">
                                    Select Package:
                                </label>
                                <select
                                    id="package"
                                    name="package"
                                    value={selectedPackage?.name || ''}
                                    onChange={handlePackageChange}
                                    className="mt-1 py-3 px-3 block w-full rounded-md border border-gray-400 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm hover:border-green-400"
                                >
                                    {productData.details.pricing.packages.map((pkg) => (
                                        <option key={pkg._id} value={pkg.name}>
                                            {pkg.name} - ${pkg.price}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Selected Package Price */}
                        {selectedPackage && (
                            <p className="mt-4 text-lg font-medium text-gray-900">
                                Selected Package Price: ${selectedPackage.price}
                            </p>
                        )}

                        {/* Add To Cart Button */}
                        <div className="mt-10">
                            <button
                                onClick={handleAddToCart}
                                type="button"
                                className={`w-full flex items-center justify-center rounded-md px-8 py-3 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${businesses.length > 0 && selectedBusiness
                                    ? "bg-green-600 hover:bg-green-700"
                                    : "bg-gray-400 cursor-not-allowed"
                                    }`}
                                disabled={businesses.length === 0 || !selectedBusiness}
                            >
                                {businesses.length > 0 && selectedBusiness
                                    ? "Add to Cart"
                                    : "Please select a business to continue"}
                            </button>
                        </div>
                    </div>
                </div>
                <div className="mx-auto mt-16 w-full max-w-2xl lg:col-span-4 lg:mt-0 lg:max-w-none">
                    <TabGroup defaultIndex={1}>
                        <div className="border-b border-gray-200">
                            <TabList className="-mb-px flex space-x-8">
                                <Tab className="whitespace-nowrap border-b-2 border-transparent py-6 text-sm font-medium text-gray-700 hover:border-gray-300 hover:text-gray-800  data-[selected]:text-green-600">
                                    Customer Reviews
                                </Tab>
                                <Tab className="whitespace-nowrap border-b-2 border-transparent py-6 text-sm font-medium text-gray-700 hover:border-gray-300 hover:text-gray-800  data-[selected]:text-green-600">
                                    Frequently Asked Questions
                                </Tab>
                                <Tab className="whitespace-nowrap border-b-2 border-transparent py-6 text-sm font-medium text-gray-700 hover:border-gray-300 hover:text-gray-800  data-[selected]:text-green-600">
                                    Terms of Service
                                </Tab>
                            </TabList>
                        </div>
                        <TabPanels as={Fragment}>
                            <TabPanel className="-mb-10">
                                <h3 className="sr-only">Customer Reviews</h3>
                                {/* Content for Customer Reviews */}
                            </TabPanel>

                            <TabPanel className="text-sm text-gray-500">
                                <h3 className="sr-only">Frequently Asked Questions</h3>
                                <dl>
                                    {productData.details.faqs.map((faq: typeof FaqType) => (
                                        <Fragment key={faq.question}>
                                            <dt className="mt-10 font-medium text-gray-900">{faq.question}</dt>
                                            <dd className="mt-2 text-sm/6 text-gray-500">
                                                <p>{faq.answer}</p>
                                            </dd>
                                        </Fragment>
                                    ))}
                                </dl>
                            </TabPanel>

                            <TabPanel className="pt-10">
                                <h3 className="sr-only">Terms of Service</h3>
                                <div className="text-sm text-gray-500 [&>:first-child]:mt-0 [&_h4]:mt-5 [&_h4]:font-medium [&_h4]:text-gray-900 [&_li::marker]:text-gray-300 [&_li]:pl-2 [&_p]:my-2 [&_p]:text-sm/6 [&_ul]:my-4 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5 [&_ul]:text-sm/6">
                                    These Terms of Service ("Terms") govern your use of the services provided by TwelveFifth, Inc, doing business as Gurulabs ("we," "us," or "our"). By accessing or using our services, you agree to these Terms. If you do not agree with any part of these Terms, please do not use our services. <br /><br />
                                    We provide tools, resources, and support through our website and other platforms. You are responsible for using our services in compliance with applicable laws and regulations. Unauthorized use of our services, including any form of unlawful or prohibited activity, may result in termination of your access. We reserve the right to update or modify these Terms at any time without prior notice, and continued use of our services constitutes acceptance of the revised Terms. For questions or concerns, contact us at support@gurulabs.dev.
                                </div>
                            </TabPanel>
                        </TabPanels>
                    </TabGroup>
                </div>

            </div>
        </div >
    );
};

export default ProductShow;
